import { AuthenticationDetails, CognitoUserPool, CognitoUser } from 'amazon-cognito-identity-js';
import routes from '@/../config/env';

const { cognitoPool } = routes;

const userPool = new CognitoUserPool(cognitoPool);
let cognitoUser = userPool.getCurrentUser();
let pendingSessionPromise = null;
let onSessionInvalid = null;

const getCachedSession = () => {
  const token = localStorage.getItem('userToken');
  if (!token) return null;

  const huddleRoles = localStorage.getItem('huddleRoles');
  return {
    token,
    userRoles: huddleRoles ? huddleRoles.split(',') : [],
    operator: localStorage.getItem('operator') || '',
  };
};

const persistSessionToken = (token) => {
  if (!token) return;
  if (localStorage.getItem('userToken') !== token) {
    localStorage.setItem('userToken', token);
  }
};

export const isCognitoNetworkError = (error) => error?.message === 'Network error';

export const setOnSessionInvalid = (callback) => {
  onSessionInvalid = callback;
};

export const notifySessionInvalid = () => {
  onSessionInvalid?.();
};

export const isUserLoggedIn = () => new Promise((resolve, reject) => {
  cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) {
    reject(new Error('No user session'));
    return;
  }
  cognitoUser.getSession((err, result) => {
    if (!err && result?.isValid()) {
      resolve({
        token: result.idToken.jwtToken,
        userRoles: result.idToken.payload['cognito:groups'],
        operator: result.idToken.payload['custom:operator_id'],
      });
    } else {
      reject(err || new Error('Invalid session'));
    }
  });
});

const fetchAuthSession = async () => {
  try {
    const session = await isUserLoggedIn();
    persistSessionToken(session.token);
    return session;
  } catch (error) {
    const cachedSession = getCachedSession();
    if (cachedSession && isCognitoNetworkError(error)) {
      return cachedSession;
    }
    throw error;
  }
};

export const getAuthSession = (options = {}) => {
  if (options.forceRefresh) {
    pendingSessionPromise = null;
    cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signInUserSession = null;
    }
  }

  if (!pendingSessionPromise) {
    pendingSessionPromise = fetchAuthSession().finally(() => {
      pendingSessionPromise = null;
    });
  }
  return pendingSessionPromise;
};

export const getUser = () => cognitoUser || userPool.getCurrentUser();

export const authenticateUser = (user) => {
  const authenticationData = {
    Username: user.username,
    Password: user.password,
  };

  const authenticationDetails = new AuthenticationDetails(
    authenticationData,
  );

  const userData = {
    Username: user.username,
    Pool: userPool,
  };
  cognitoUser = new CognitoUser(userData);

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        const idToken = session.idToken.jwtToken;
        resolve({
          message: 'token',
          data: {
            token: idToken,
            userRoles: session.idToken.payload['cognito:groups'],
            operator: session.idToken.payload['custom:operator_id'],
          },
        });
      },
      onFailure: (err) => {
        reject(err.message || JSON.stringify(err));
      },
      newPasswordRequired: (userAttributes) => {
        resolve({ message: 'newPasswordRequired', data: userAttributes });
      },
    });
  });
};

export const changePassword = (payload) => new Promise((resolve, reject) => {
  const data = payload;
  delete data.userAttributes?.email;
  delete data.userAttributes?.email_verified;
  cognitoUser.completeNewPasswordChallenge(data.newPassword, data.userAttributes, {
    onSuccess: (result) => {
      resolve(result);
    },
    onFailure: (err) => {
      reject(err);
    },
  });
});

export const requestCode = (username) => new Promise((resolve, reject) => {
  const userData = {
    Username: username,
    Pool: userPool,
  };
  cognitoUser = new CognitoUser(userData);
  cognitoUser.forgotPassword({
    onSuccess: (result) => {
      resolve(result);
    },
    onFailure: (err) => {
      reject(err);
    },
    inputVerificationCode() {
      resolve();
    },
  });
});

export const confirmNewPassword = (payload) => new Promise((resolve, reject) => {
  const userData = {
    Username: payload.username,
    Pool: userPool,
  };
  cognitoUser = new CognitoUser(userData);
  cognitoUser.confirmPassword(payload.verificationCode, payload.newPassword, {
    onSuccess: (result) => {
      resolve(result);
    },
    onFailure: (err) => {
      reject(err);
    },
  });
});

export const logout = () => {
  if (cognitoUser) cognitoUser.signOut();
};
